import React, { useEffect, useState } from 'react';
import {
  Button,
  Input,
  FullDarkLogo,
  FullLightLogo,
  GlobalBaseTheme,
  ToastMode,
  useToasts,
  ToastProvider,
  useTheme,
  classNames,
} from '@groma-dev/blueprint';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import Cookies from 'js-cookie';
import { FormattedMessage, useIntl } from 'react-intl';
import { parseLocationSearch } from '@groma-dev/scaffold';
import { AppConfig } from '../../../core/AppConfig';
import { AppRoutes } from '../../../core/routes';
import { ComponentFactory } from '../../ComponentFactory';
import { buildUrlWithParams } from '../../utils/WebHelper';
import styles from './Auth.module.scss';

export interface AuthProps {}

const AuthForm = () => {
  const { addToast } = useToasts();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const [password, setPassword] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { password: appPassword, cookieName, protectionKey } = AppConfig.Settings.Server.passwordProtection;

  const handleOnChange = (e: any) => {
    setPassword(e.target.value);
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setIsSubmitting(true);
    const isMatch = password === appPassword;

    if (isMatch) {
      setIsSubmitting(false);
      setPassword('');
      Cookies.set(cookieName, protectionKey);
      const { return_url } = parseLocationSearch(location.search);
      navigate(buildUrlWithParams(return_url ?? AppRoutes.root));
    } else {
      addToast(
        formatMessage({
          id: 'auth.passwordError',
          defaultMessage: 'Invalid Password. Try again or contact an administrator for assistance.',
          description: 'Invalid password toast message',
        }),
        ToastMode.ERROR
      );
      setIsSubmitting(false);
    }
  };

  return (
    <div className={classNames(styles.formContainer)}>
      <h1 className={classNames(styles.content)}>
        <FormattedMessage
          id="auth.title"
          defaultMessage="Please authenticate to view this application."
          description="Auth page title"
        />
      </h1>
      <form onSubmit={handleSubmit}>
        <div className={classNames(styles.inputContainer)}>
          <Input name="password" label="App Password" type="password" onChange={handleOnChange} value={password} />
        </div>
        <Button fullWidth isBusy={isSubmitting} disabled={password.trim().length === 0} onClick={handleSubmit}>
          <FormattedMessage id="auth.submit" defaultMessage="Submit" description="Auth page submit button text" />
        </Button>
      </form>
    </div>
  );
};

const Auth = observer(() => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [initialized, setInitialized] = useState<boolean>(false);
  const isDarkTheme = theme.__base === GlobalBaseTheme.DARK;

  useEffect(() => {
    if (!initialized) {
      const { cookieName, enabled: appProtectionEnabled } = AppConfig.Settings.Server.passwordProtection;
      const appPasswordCookiePresent = Cookies.get(cookieName);

      if (appPasswordCookiePresent || !appProtectionEnabled) {
        navigate(buildUrlWithParams(AppRoutes.root), { replace: true });
      } else setInitialized(true);
    }
  });

  return !initialized ? (
    <ComponentFactory.Loading isPage />
  ) : (
    <ToastProvider offsetTop={0}>
      <main className={classNames(styles.mainContainer)}>
        <div className={classNames(styles.mainWrapper)}>
          <div className={classNames(styles.logoContainer)}>
            {isDarkTheme ? (
              <FullLightLogo className={classNames(styles.logo)} />
            ) : (
              <FullDarkLogo className={classNames(styles.logo)} />
            )}
          </div>
          <AuthForm />
        </div>
      </main>
    </ToastProvider>
  );
});

export default Auth;
